<template>
  <div class="csr-page">

    <div class="csr-page-header">
      <h1 class="csr-page-title">Финансовый отчёт</h1>
    </div>

    <q-banner v-if="errorText" rounded class="error-banner q-mb-sm">{{ errorText }}</q-banner>

    <div class="csr-filters-card">
      <div class="csr-filters-card__body">
        <div class="csr-filters-toolbar">
          <div class="csr-filters-main">
            <CustomSelect
              :options="residentOptions"
              option-label-key="label"
              option-value-key="value"
              label="ЖК"
              :value="filterResident"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterResident"
              class="csr-filter-field"
              @change="(v) => filterResident = v"
            />
            <CustomSelect
              :options="projectTypeOptions"
              option-label-key="label"
              option-value-key="value"
              label="Тип проекта"
              :value="filterProjectType"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterProjectType"
              class="csr-filter-field"
              @change="(v) => filterProjectType = v"
            />
            <CustomSelect
              :options="contractorOptions"
              option-label-key="label"
              option-value-key="value"
              label="Контрагент"
              :value="filterContractor"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterContractor"
              class="csr-filter-field"
              @change="(v) => filterContractor = v"
            />
            <CustomSelect
              :options="statusOptions"
              option-label-key="label"
              option-value-key="value"
              label="Статус"
              :value="filterStatus"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filterStatus"
              class="csr-filter-field"
              @change="(v) => filterStatus = v"
            />
          </div>

          <div class="csr-filters-actions">
            <q-btn
              class="csr-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!hasActiveFilters"
              @click="resetFilters"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Статистика + легенда -->
    <div class="stats-row q-mb-sm">
      <div class="stats-row__count">
        Строк: <strong>{{ filteredRows.length }}</strong>
        <span v-if="filteredRows.length !== rows.length" class="stats-row__total-hint">
          из {{ rows.length }}
        </span>
      </div>
      <div class="legend">
        <q-chip dense class="legend-chip legend-chip--green">
          <q-icon name="check_circle" size="13px" class="q-mr-xs" />
          Полностью оплачено
          <q-tooltip>Оплачено по акту сверки ≥ сданных работ, нет ожидаемой оплаты</q-tooltip>
        </q-chip>
        <q-chip dense class="legend-chip legend-chip--yellow">
          <q-icon name="schedule" size="13px" class="q-mr-xs" />
          Ожидается оплата
          <q-tooltip>Есть непогашенная задолженность по оплате</q-tooltip>
        </q-chip>
      </div>
    </div>

    <!-- Таблица -->
    <CustomTable
      title=""
      :rowsData="filteredRows"
      :columns="columns"
      :loading="loading"
      :small="true"
      :custom-body="true"
      :bottom-row="true"
      :sticky-header="true"
    >
      <template #customBody="props">
        <q-tr :class="rowClass(props.row)">

          <CustomTableCell align="center" vertical-align="center">
            <span class="row-num">{{ props.row.project_id }}</span>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            <div class="cell-jk">
              <q-icon name="apartment" size="13px" color="blue-grey-4" />
              {{ props.row.resident_name || '—' }}
            </div>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            {{ props.row.project_type_name || '—' }}
          </CustomTableCell>

          <!-- Статус проекта -->
          <CustomTableCell align="center" vertical-align="center">
            <q-chip
              dense
              :class="statusChipClass(props.row.project_status_id)"
              class="status-chip-project"
            >
              {{ props.row.project_status_name || '—' }}
              <q-tooltip>Статус проекта</q-tooltip>
            </q-chip>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            <div class="cell-contractor">
              <q-icon
                v-if="props.row.contractor_name === 'Нет Субподряда'"
                name="remove_circle_outline" size="13px" color="grey-4"
              />
              <q-icon v-else name="handshake" size="13px" color="primary" />
              <span :class="{ 'text-grey-5': props.row.contractor_name === 'Нет Субподряда' }" class="q-ml-xs">
                {{ props.row.contractor_name }}
              </span>
            </div>
          </CustomTableCell>

          <CustomTableCell align="left" vertical-align="center">
            <Clickable
              v-if="props.row.agreement_num && props.row.project_id"
              link
              altMode
              hideUnderline
              :href="buildProjectLink(props.row)"
            >
              <span class="agreement-num agreement-num--link">{{ props.row.agreement_num }}</span>
            </Clickable>
            <span v-else class="agreement-num">{{ props.row.agreement_num || '—' }}</span>
          </CustomTableCell>

          <!-- Сумма договора -->
          <CustomTableCell align="right" vertical-align="center">
            <span class="amount">{{ fmt(props.row.agreement_sum) }}</span>
          </CustomTableCell>

          <!-- Сдано работ + прогресс-бар -->
          <CustomTableCell align="right" vertical-align="center">
            <div class="amount-cell">
              <span class="amount">{{ fmt(props.row.done_sum) }}</span>
              <div class="mini-bar">
                <div
                  class="mini-bar__fill mini-bar__fill--done"
                  :style="{ width: pct(props.row.done_sum, props.row.agreement_sum) }"
                >
                  <q-tooltip>{{ pct(props.row.done_sum, props.row.agreement_sum) }} от суммы договора</q-tooltip>
                </div>
              </div>
            </div>
          </CustomTableCell>

          <!-- Оплачено по акту сверки + прогресс-бар -->
          <CustomTableCell align="right" vertical-align="center">
            <div class="amount-cell">
              <span class="amount">{{ fmt(props.row.debit_sum) }}</span>
              <div class="mini-bar">
                <div
                  class="mini-bar__fill mini-bar__fill--paid"
                  :style="{ width: pct(props.row.debit_sum, props.row.done_sum) }"
                >
                  <q-tooltip>{{ pct(props.row.debit_sum, props.row.done_sum) }} от сданных работ</q-tooltip>
                </div>
              </div>
            </div>
          </CustomTableCell>

          <!-- Ожидается оплата -->
          <CustomTableCell align="right" vertical-align="center">
            <div class="expected-payment-cell">
              <div v-if="isZero(props.row.expected_payment_without_warranty)" class="badge badge--ok">
                <q-icon name="check_circle" size="13px" />
                0
              </div>
              <div v-else class="badge badge--warn">
                <q-icon name="schedule" size="13px" />
                {{ fmt(props.row.expected_payment_without_warranty) }}
                <q-tooltip>Без гарантии: {{ fmt(props.row.expected_payment_without_warranty) }}</q-tooltip>
              </div>
              <div v-if="props.row.expected_payment_with_warranty > 0" class="expected-payment-info">
                <SvgIcon name="info" size="14px" class="expected-payment-info__icon" />
                <q-popup-proxy>
                  <q-banner class="tw-p-2">
                    <div class="tw-text-sm tw-text-slate-500 tw-mb-1">Сумма с гарантией</div>
                    <div class="tw-font-semibold">{{ fmt(props.row.expected_payment_with_warranty) }}</div>
                  </q-banner>
                </q-popup-proxy>
              </div>
            </div>
          </CustomTableCell>

          <!-- Закрыто по АВР -->
          <CustomTableCell align="right" vertical-align="center">
            <span class="amount">{{ fmt(props.row.credit_sum) }}</span>
          </CustomTableCell>

          <!-- Можно закрыть АВР -->
          <CustomTableCell align="right" vertical-align="center">
            <span class="amount">{{ fmt(props.row.avr_closable_sum) }}</span>
            <q-tooltip>Сумма полностью принятых работ (is_checked), 100% call_sum</q-tooltip>
          </CustomTableCell>

          <!-- Ошибка по платежам -->
          <CustomTableCell align="left" vertical-align="top" class="payment-error-cell">
            <template v-if="paymentErrorText(props.row)">
              <div v-if="!isPaymentErrorExpanded(props.row)" class="payment-error-wrap">
                <div class="payment-error-text payment-error-text--clamped">{{ paymentErrorText(props.row) }}</div>
                <q-btn
                  flat dense no-caps size="sm" class="payment-error-toggle"
                  icon="expand_more" label="Развернуть"
                  @click.stop="togglePaymentError(props.row)"
                />
              </div>
              <div v-else class="payment-error-wrap">
                <div class="payment-error-text payment-error-text--full">{{ paymentErrorText(props.row) }}</div>
                <q-btn
                  flat dense no-caps size="sm" class="payment-error-toggle"
                  icon="expand_less" label="Свернуть"
                  @click.stop="togglePaymentError(props.row)"
                />
              </div>
            </template>
          </CustomTableCell>

        </q-tr>
      </template>

      <!-- Итоговая строка -->
      <template #bottomRow>
        <q-tr class="csr-total-row">
          <q-td colspan="6" class="csr-total-row__label">Итого ({{ filteredRows.length }} строк)</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('agreement_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('done_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('debit_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">
            <div v-if="isZero(sum('expected_payment_without_warranty'))" class="badge badge--ok">
              <q-icon name="check_circle" size="13px" /> 0
            </div>
            <div v-else class="badge badge--warn">{{ fmt(sum('expected_payment_without_warranty')) }}</div>
          </q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('credit_sum')) }}</q-td>
          <q-td class="csr-total-row__cell text-right">{{ fmt(sum('avr_closable_sum')) }}</q-td>
          <q-td class="csr-total-row__cell csr-total-row__cell--empty" />
        </q-tr>
      </template>
    </CustomTable>

  </div>
</template>

<script>
import instance from "@/services/api";
import CustomSelect from "@/components/form/CustomSelect";
import CustomTable from "@/components/common/CustomTable";
import CustomTableCell from "@/components/common/CustomTable/custom-table-cell";
import Clickable from "@/components/common/Clickable.vue";
import SvgIcon from "@/components/common/SvgIcon";

const STATUS_META = {
  100: { label: "Создан",    cls: "status--created"   },
  200: { label: "Старт СМР", cls: "status--active"    },
  300: { label: "Завершен",  cls: "status--done"      },
  400: { label: "Отменен",   cls: "status--cancelled" },
};

const columns = [
  { name: "project_id",         label: "ID",                 field: "project_id",         align: "center", style: "width:52px" },
  { name: "resident_name",      label: "ЖК",                field: "resident_name",      align: "left"  },
  { name: "project_type_name",  label: "Тип проекта",       field: "project_type_name",  align: "left"  },
  { name: "project_status_name",label: "Статус",            field: "project_status_name",align: "center"},
  { name: "contractor_name",    label: "Контрагент",        field: "contractor_name",    align: "left"  },
  { name: "agreement_num",      label: "Договор",           field: "agreement_num",      align: "left"  },
  { name: "agreement_sum",      label: "Сумма договора",    field: "agreement_sum",      align: "right" },
  { name: "done_sum",           label: "Сдано работ",       field: "done_sum",           align: "right" },
  { name: "debit_sum",          label: "Оплачено по акту сверки", field: "debit_sum",          align: "right" },
  { name: "expected_payment_without_warranty",   label: "Ожидается оплата",  field: "expected_payment_without_warranty",   align: "right" },
  { name: "credit_sum",         label: "Закрыто по АВР",       field: "credit_sum",         align: "right" },
  { name: "avr_closable_sum",   label: "Можно закрыть АВР", field: "avr_closable_sum",   align: "right" },
  { name: "payment_error_info", label: "Ошибка по платежам",  field: "payment_error_info", align: "left", style: "min-width:160px;max-width:320px" },
];

export default {
  name: "ContractorSbsReport",
  components: { CustomSelect, CustomTable, CustomTableCell, Clickable, SvgIcon },
  data() {
    return {
      rows: [],
      columns,
      loading: false,
      errorText: "",
      filterResident: null,
      filterProjectType: null,
      filterContractor: null,
      filterStatus: null,
      paymentErrorExpanded: {},
    };
  },
  computed: {
    residentOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.resident_name && !seen.has(r.resident_name) && seen.add(r.resident_name))
        .map((r) => ({ label: r.resident_name, value: r.resident_name }));
    },
    projectTypeOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.project_type_name && !seen.has(r.project_type_name) && seen.add(r.project_type_name))
        .map((r) => ({ label: r.project_type_name, value: r.project_type_name }));
    },
    contractorOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.contractor_name && !seen.has(r.contractor_name) && seen.add(r.contractor_name))
        .map((r) => ({ label: r.contractor_name, value: r.contractor_name }));
    },
    statusOptions() {
      const seen = new Set();
      return this.rows
        .filter((r) => r.project_status_name && !seen.has(r.project_status_name) && seen.add(r.project_status_name))
        .map((r) => ({ label: r.project_status_name, value: r.project_status_name }));
    },
    hasActiveFilters() {
      return !!(this.filterResident || this.filterProjectType || this.filterContractor || this.filterStatus);
    },
    filteredRows() {
      return this.rows.filter((r) => {
        if (this.filterResident && r.resident_name !== this.filterResident) return false;
        if (this.filterProjectType && r.project_type_name !== this.filterProjectType) return false;
        if (this.filterContractor && r.contractor_name !== this.filterContractor) return false;
        if (this.filterStatus && r.project_status_name !== this.filterStatus) return false;
        return true;
      });
    },
  },
  async mounted() {
    await this.fetchData();
  },
  methods: {
    fmt(value) {
      const n = Number(value);
      if (!Number.isFinite(n)) return "—";
      if (n === 0) return "0";
      return n.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    isZero(value) {
      const n = Number(value);
      return Number.isFinite(n) && n === 0;
    },
    pct(part, total) {
      const p = Number(part);
      const t = Number(total);
      if (!t || !Number.isFinite(p) || !Number.isFinite(t)) return "0%";
      return `${Math.min(100, Math.round((p / t) * 100))}%`;
    },
    sum(field) {
      return this.filteredRows.reduce((acc, r) => acc + (Number(r[field]) || 0), 0);
    },
    rowClass(row) {
      const debit = Number(row.debit_sum) || 0;
      const done  = Number(row.done_sum) || 0;
      const exp   = Number(row.expected_payment_without_warranty) || 0;
      if (debit >= done && exp === 0) return "csr-tr--fully-paid";
      if (exp > 0) return "csr-tr--pending";
      return "";
    },
    statusChipClass(statusId) {
      return STATUS_META[statusId]?.cls || "status--default";
    },
    buildProjectLink(row) {
      const params = new URLSearchParams();
      if (row.resident_id)         params.set("resident_id", row.resident_id);
      if (row.project_type_id)     params.set("project_type_id", row.project_type_id);
      if (row.project_entrance_id) params.set("project_entrance_id", row.project_entrance_id);
      if (row.project_id)          params.set("project_id", row.project_id);
      if (row.sbs_project_id)      params.set("sbs_project_id", row.sbs_project_id);
      params.set("tab", "M__ProjectFormRemontCostTab");
      return `/residential-settings?${params.toString()}`;
    },
    resetFilters() {
      this.filterResident = null;
      this.filterProjectType = null;
      this.filterContractor = null;
      this.filterStatus = null;
    },
    paymentErrorText(row) {
      const t = row?.payment_error_info;
      if (t == null) return "";
      const s = String(t).trim();
      return s;
    },
    paymentErrorRowKey(row) {
      return [row?.project_id, row?.sbs_project_id, row?.agreement_num].join("|");
    },
    isPaymentErrorExpanded(row) {
      return !!this.paymentErrorExpanded[this.paymentErrorRowKey(row)];
    },
    togglePaymentError(row) {
      const k = this.paymentErrorRowKey(row);
      const next = !this.paymentErrorExpanded[k];
      this.paymentErrorExpanded = { ...this.paymentErrorExpanded, [k]: next };
    },
    async fetchData() {
      this.loading = true;
      this.errorText = "";
      try {
        const res = await instance()
          .get("/finance_payment/contractor-sbs-report/read/")
          .then((r) => r?.data);
        this.rows = res?.data || [];
        this.paymentErrorExpanded = {};
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || "Ошибка загрузки данных";
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.csr-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  box-sizing: border-box;
}

.csr-page-header {
  margin-bottom: 8px;
}

.csr-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.error-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
  font-size: 13px;
}

.csr-filters-card {
  margin-bottom: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.csr-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.csr-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.csr-filters-main {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.csr-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.csr-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper) {
    width: 100%;
  }

  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }

  :deep(.q-field__native) {
    min-height: 24px;
  }
}

.csr-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  min-width: 118px;
  padding: 0 8px;
}

@media (max-width: 1280px) {
  .csr-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .csr-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .csr-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .csr-filters-main {
    grid-template-columns: 1fr;
  }

  .csr-filters-actions {
    width: 100%;
  }

  .csr-filter-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }
}

/* Статистика + легенда */
.stats-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.stats-row__count {
  font-size: 13px;
  color: #475569;
  font-weight: 500;
}

.stats-row__total-hint {
  font-weight: 400;
  color: #94a3b8;
  margin-left: 4px;
}

.legend {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.legend-chip {
  font-size: 11px !important;
  font-weight: 500 !important;
  cursor: default;

  &--green { background: #dcfce7 !important; color: #15803d !important; }
  &--yellow { background: #fef3c7 !important; color: #b45309 !important; }
  &--blue { background: #dbeafe !important; color: #1d4ed8 !important; }
}

/* Цвета строк */
:deep(.csr-tr--fully-paid td) {
  background: #f0fdf4 !important;
  border-left: 3px solid #22c55e;
}
:deep(.csr-tr--pending td) {
  background: #fffbeb !important;
  border-left: 3px solid #f59e0b;
}
:deep(.csr-tr--avr td) {
  background: #eff6ff !important;
  border-left: 3px solid #3b82f6;
}

/* Номер строки */
.row-num {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f1f5f9;
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
}

/* ЖК */
.cell-jk {
  display: flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
}

/* Контрагент */
.cell-contractor {
  display: flex;
  align-items: center;
}

/* Статус проекта */
.status-chip-project {
  font-size: 11px !important;
  font-weight: 600 !important;
  padding: 0 8px !important;
  height: 20px !important;
}

:deep(.status--created)   { background: #f1f5f9 !important; color: #475569 !important; }
:deep(.status--active)    { background: #dcfce7 !important; color: #15803d !important; }
:deep(.status--done)      { background: #dbeafe !important; color: #1d4ed8 !important; }
:deep(.status--cancelled) { background: #fee2e2 !important; color: #b91c1c !important; }
:deep(.status--default)   { background: #f1f5f9 !important; color: #94a3b8 !important; }

/* Номер договора */
.agreement-num {
  font-family: monospace;
  font-size: 11px;
  background: #f8fafc;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
  white-space: nowrap;
}

.agreement-num--link {
  color: #1d4ed8;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
  }
}

/* Суммы */
.amount {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  white-space: nowrap;
}

.amount-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 3px;
}

/* Мини-бар */
.mini-bar {
  width: 60px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 99px;
  overflow: hidden;
}

.mini-bar__fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.35s ease;

  &--done { background: linear-gradient(90deg, #6366f1, #8b5cf6); }
  &--paid { background: linear-gradient(90deg, #10b981, #22c55e); }
}

/* Ожидаемая оплата с info */
.expected-payment-cell {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.expected-payment-info {
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.expected-payment-info__icon {
  color: #64748b;
  transition: color 0.15s;
}

.expected-payment-info:hover .expected-payment-info__icon {
  color: #0f172a;
}

/* Бэджи */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 7px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &--ok   { background: #dcfce7; color: #15803d; }
  &--warn { background: #fef3c7; color: #b45309; }
  &--info { background: #dbeafe; color: #1d4ed8; }
}

/* Итоговая строка */
:deep(.csr-total-row) {
  position: sticky;
  bottom: 0;
  z-index: 4;
}

:deep(.csr-total-row td) {
  background: #f1f5f9 !important;
  color: #1e293b !important;
  font-weight: 700 !important;
  font-size: 12px !important;
  border-top: 2px solid #cbd5e1 !important;
}

.csr-total-row__label {
  color: #64748b !important;
  font-size: 11px !important;
  font-weight: 600 !important;
}

.csr-total-row__cell {
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum" 1;
  white-space: nowrap;
}

.csr-total-row__cell--empty {
  border-top: 2px solid #cbd5e1 !important;
}

/* Ошибка по платежам */
.payment-error-cell {
  font-size: 11px;
  color: #475569;
}

.payment-error-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.payment-error-text {
  word-break: break-word;
  line-height: 1.35;
}

.payment-error-text--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.payment-error-text--full {
  white-space: pre-wrap;
  max-height: 280px;
  overflow: auto;
}

.payment-error-toggle {
  margin-top: 2px;
  font-size: 11px !important;
  min-height: 26px !important;
  padding: 0 6px !important;
}
</style>
